import { useEffect, useRef, useState } from 'react'
import { UploadItem } from '@wellesley/uploads'

type UseUploadTrackingProps = {
  uploadingItems: UploadItem[]
  onComplete: (completedItems: UploadItem[]) => void
  onAllCanceled: () => void
}

type UseUploadTrackingResult = {
  completedUploadIds: Map<string, string>
  isItemCompleted: (itemId: string) => boolean
  isItemError: (itemId: string) => boolean
}

export const useUploadTracking = ({
  uploadingItems,
  onComplete,
  onAllCanceled,
}: UseUploadTrackingProps): UseUploadTrackingResult => {
  const initialItemsRef = useRef<UploadItem[] | null>(null)
  const hasCompletedRef = useRef(false)
  const [completedUploadIds, setCompletedUploadIds] = useState<
    Map<string, string>
  >(new Map())

  useEffect(() => {
    if (initialItemsRef.current === null && uploadingItems.length > 0) {
      initialItemsRef.current = [...uploadingItems]

      uploadingItems.forEach((item) => {
        item.upload
          .then((uploadId) => {
            if (uploadId) {
              setCompletedUploadIds((prev) => {
                const next = new Map(prev)
                next.set(item.id, uploadId)
                return next
              })
            }
          })
          .catch(() => {
            setCompletedUploadIds((prev) => {
              const next = new Map(prev)
              next.set(item.id, '')
              return next
            })
          })
      })
    }
  }, [uploadingItems])

  useEffect(() => {
    if (hasCompletedRef.current) return

    const initialItems = initialItemsRef.current
    if (!initialItems || initialItems.length === 0) return

    const remainingItems = uploadingItems.filter((item) =>
      initialItems.some((init) => init.id === item.id),
    )

    if (remainingItems.length === 0) {
      hasCompletedRef.current = true
      onAllCanceled()
      return
    }

    const allCompleted = remainingItems.every((item) =>
      completedUploadIds.has(item.id),
    )

    if (allCompleted) {
      const successfulItems = remainingItems
        .filter((item) => {
          const uploadId = completedUploadIds.get(item.id)
          return uploadId && uploadId.length > 0
        })
        .map((item) => ({
          ...item,
          uploadId: completedUploadIds.get(item.id),
        }))

      hasCompletedRef.current = true
      onComplete(successfulItems)
    }
  }, [uploadingItems, completedUploadIds, onComplete, onAllCanceled])

  const isItemCompleted = (itemId: string): boolean => {
    return completedUploadIds.has(itemId)
  }

  const isItemError = (itemId: string): boolean => {
    const isCompleted = completedUploadIds.has(itemId)
    const uploadId = completedUploadIds.get(itemId)
    return isCompleted && (!uploadId || uploadId.length === 0)
  }

  return {
    completedUploadIds,
    isItemCompleted,
    isItemError,
  }
}
